import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import ExcelJS from 'exceljs';
import { Container } from 'react-bootstrap';

const API = 'https://us-central1-inprojuy-f1173.cloudfunctions.net/app';

const norm = (v) => (v === null || v === undefined ? '' : v);

const BDAutoexcluidos = () => {
  const [autoexcluidos, setAutoexcluidos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const cargar = async () => {
      const [autoRes, empRes] = await Promise.allSettled([
        axios.get(`${API}/autoexcluidost`),
        axios.get(`${API}/empleados`),
      ]);

      if (autoRes.status === 'fulfilled' && Array.isArray(autoRes.value.data)) {
        setAutoexcluidos(autoRes.value.data);
      } else if (autoRes.status === 'rejected') {
        console.error('Error al obtener autoexcluidos:', autoRes.reason?.message);
      }

      if (empRes.status === 'fulfilled' && Array.isArray(empRes.value.data)) {
        setEmpleados(empRes.value.data);
      } else if (empRes.status === 'rejected') {
        console.error('Error al obtener empleados:', empRes.reason?.message);
      }

      setLoading(false);
    };

    cargar();
  }, []);

  // Base unificada: autoexcluidos (voluntarios) + empleados (excluidos), con columna Tipo.
  const combinados = useMemo(() => {
    const filasAuto = autoexcluidos.map((a) => ({
      rowId: `auto-${a.id ?? a.dni}`,
      tipo: 'Autoexcluido',
      nombre: norm(a.nombre),
      dni: norm(a.dni),
      fechaNacimiento: norm(a.fechaNacimiento),
      vencimiento: norm(a.vencimiento),
      telefono: norm(a.telefono),
    }));

    const filasEmp = empleados.map((e) => ({
      rowId: `emp-${e.id ?? e.dni}`,
      tipo: 'Empleado',
      nombre: `${norm(e.nombre)} ${norm(e.apellido)}`.trim(),
      dni: norm(e.dni),
      fechaNacimiento: '',
      vencimiento: '',
      telefono: '',
    }));

    return [...filasAuto, ...filasEmp];
  }, [autoexcluidos, empleados]);

  const columns = [
    { field: 'tipo', headerName: 'Tipo', width: 130 },
    { field: 'nombre', headerName: 'Nombre y Apellido', width: 230 },
    { field: 'dni', headerName: 'DNI', width: 130 },
    { field: 'fechaNacimiento', headerName: 'Fecha de Nacimiento', width: 170 },
    { field: 'vencimiento', headerName: 'Vencimiento', width: 150 },
    { field: 'telefono', headerName: 'Teléfono', width: 140 },
  ];

  const exportToExcel = async () => {
    const thin = {
      top: { style: 'thin', color: { argb: 'FFDDDDDD' } },
      left: { style: 'thin', color: { argb: 'FFDDDDDD' } },
      bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } },
      right: { style: 'thin', color: { argb: 'FFDDDDDD' } },
    };

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Base de exclusiones', {
      views: [{ state: 'frozen', ySplit: 7 }],
    });

    // Anchos de columna
    const anchos = [16, 34, 14, 20, 16, 16];
    anchos.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    // Logo (arriba a la izquierda). Si falla, se exporta igual sin logo.
    try {
      const resp = await fetch('/img/inprojuylogo.png');
      const blob = await resp.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      const base64 = String(dataUrl).includes(',') ? String(dataUrl).split(',')[1] : String(dataUrl);
      const imgId = wb.addImage({ base64, extension: 'png' });
      ws.addImage(imgId, { tl: { col: 0.15, row: 0.2 }, ext: { width: 250, height: 95 } });
    } catch (e) {
      console.warn('No se pudo incrustar el logo en el Excel:', e?.message);
    }
    ws.getRow(1).height = 26;
    ws.getRow(2).height = 26;
    ws.getRow(3).height = 26;

    // Título
    ws.mergeCells('A4:F4');
    const titulo = ws.getCell('A4');
    titulo.value = 'Base de Exclusiones';
    titulo.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF14539A' } };
    titulo.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(4).height = 24;

    // Subtítulo con fecha
    ws.mergeCells('A5:F5');
    const sub = ws.getCell('A5');
    const hoy = new Date().toLocaleDateString('es-AR');
    sub.value = `INPROJUY · Autoexcluidos y Empleados · Generado el ${hoy}`;
    sub.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF666666' } };
    sub.alignment = { horizontal: 'center' };

    // Encabezado (fila 7)
    const headers = ['Tipo', 'Nombre y Apellido', 'DNI', 'Fecha de Nacimiento', 'Vencimiento', 'Teléfono'];
    const headerRow = ws.getRow(7);
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = thin;
    });
    headerRow.height = 20;

    // Datos
    combinados.forEach((r) => {
      const row = ws.addRow([r.tipo, r.nombre, r.dni, r.fechaNacimiento, r.vencimiento, r.telefono]);
      row.eachCell((cell) => { cell.border = thin; });
      const esEmpleado = r.tipo === 'Empleado';
      const tipoCell = row.getCell(1);
      tipoCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: esEmpleado ? 'FFFFE0B2' : 'FFE3F2FD' } };
      tipoCell.font = { bold: true, color: { argb: esEmpleado ? 'FFB25E00' : 'FF14539A' } };
      tipoCell.alignment = { horizontal: 'center' };
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blobXlsx = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blobXlsx);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'base-exclusiones.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container style={{ height: 650, width: '100%' }}>
      <div style={{ height: 500, width: '100%' }}>
        <center><h2>Base de Datos de Autoexcluidos</h2></center>
        <button onClick={exportToExcel} disabled={loading || combinados.length === 0}>
          Exportar a Excel
        </button>
        <p></p>

        {loading ? (
          <div id="load">
            <div>O</div>
            <div>D</div>
            <div>N</div>
            <div>A</div>
            <div>G</div>
            <div>R</div>
            <div>A</div>
            <div>C</div>
          </div>
        ) : (
          <DataGrid
            rows={combinados}
            columns={columns}
            pageSize={pageSize}
            pagination
            onPageSizeChange={(params) => {
              setPageSize(params.pageSize);
            }}
            getRowId={(row) => row.rowId}
          />
        )}
      </div>
    </Container>
  );
};

export default BDAutoexcluidos;
